import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSnackbar } from "notistack";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { Box, Button, Stack, Typography } from "@mui/material";

import BlogPostAuthors from "./components/BlogPostAuthors.jsx";
import BlogPostPermissions from "./components/BlogPostPermissions.jsx";
import BlogPostStatusChanger from "./components/BlogPostStatusChanger.jsx";
import { useBlogPostActions } from "../../../hooks/useBlogPostActions.jsx";
import BlogPostSettings from "./components/BlogPostSettings.jsx";
import BlogPostTags from "./components/BlogPostTags.jsx";
import BlogBodyRenderer from "./components/BlogBodyRenderer.jsx";

export default function BlogPostShowPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();

  const { findById, state: blogState } = useBlogPostActions();
  const blogPost = blogState.data.data || null;

  const { blog_post_id: blogPostId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);

  const blogPostDisplayName = blogPost
    ? `${blogPost.overline ? `${blogPost.overline}: ` : ""}${blogPost.headline}` ||
      `Post #${blogPostId}`
    : `Post #${blogPostId}`;

  const authors = blogPost?.authors ? blogPost.authors : [];

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Einstellungen", path: "/admin/settings" },
      { name: "News-Verwaltung", path: "/admin/blog" },
      {
        name: blogPostDisplayName,
        path: `/admin/blog/${blogPostId}`,
      },
    ]);
  }, [setBreadcrumbs, blogPostDisplayName, blogPostId]);

  const fetchData = useCallback(async () => {
    await findById(blogPostId, {
      setIsLoading: setIsLoading,
      options: {
        params: {
          include: ["authors", "customers", "creator", "roles", "tags"],
        },
      },
    });
  }, [blogPostId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClickEdit = () => {
    navigate(`/admin/blog/${blogPost.id}/edit`);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!blogPost) {
    return (
      <Box>
        <Typography variant="h3" sx={{ mb: 3 }}>
          News-Beitrag nicht gefunden
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={4}
        sx={{ maxWidth: 600, margin: "auto" }}
        pb={4}
      >
        <Box>
          <Box flexDirection="column" display="flex">
            <Typography variant="caption" sx={{ mb: 1 }}>
              Status
            </Typography>
            <BlogPostStatusChanger blogPost={blogPost} />
          </Box>
        </Box>

        <Box flexDirection="row" display="flex" marginLeft="auto">
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickEdit}
              disabled={isLoading}
            >
              {isLoading ? "Wird geladen..." : "Inhalt bearbeiten"}
            </Button>
          </Box>

          <BlogPostSettings blogPost={blogPost} />
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="left"
        spacing={4}
        pb={6}
        sx={{ maxWidth: 600, margin: "auto" }}
      >
        <Box flexDirection="column" display="flex">
          <Typography variant="caption" sx={{ mb: 1 }}>
            Autoren
          </Typography>
          <BlogPostAuthors authors={blogPost.authors} blogPost={blogPost} />
        </Box>

        <Box flexDirection="column" display="flex">
          <Typography variant="caption" sx={{ mb: 1 }}>
            Rechte
          </Typography>
          <BlogPostPermissions authors={blogPost.authors} blogPost={blogPost} />
        </Box>

        <Box flexDirection="column" display="flex" flexGrow={1}>
          <Typography variant="caption" sx={{ mb: 1 }}>
            Tags
          </Typography>
          <BlogPostTags blogPost={blogPost} />
        </Box>
      </Stack>

      <Stack
        direction="column"
        justifyContent="center"
        spacing={4}
        sx={{ maxWidth: 600, margin: "auto" }}
      >
        <Box sx={{ maxWidth: 600, margin: "auto" }}>
          <Typography variant="caption" sx={{ mb: 3 }}>
            {blogPost.overline}
          </Typography>

          <Typography variant="h2" sx={{ mb: 3 }}>
            {blogPost.headline}
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            {blogPost.teaser}
          </Typography>

          <BlogBodyRenderer body={blogPost.body} />
        </Box>
      </Stack>
    </>
  );
}
